import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { ExcelUploadModal } from '../../components/admin/ExcelUploadModal';
import { API_BASE } from '../../config/api';

export const ScholarshipList = () => {
  const navigate = useNavigate();
  const [scholarships, setScholarships] = useState([]);
  const [filteredScholarships, setFilteredScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterPublished, setFilterPublished] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchScholarships();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [scholarships, filterProvider, filterType, filterPublished]);

  const fetchScholarships = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/scholarships`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch scholarships');

      const data = await response.json();
      setScholarships(data.scholarships || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...scholarships];

    if (filterProvider !== 'all') {
      filtered = filtered.filter(sch => sch.provider === filterProvider);
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(sch => sch.scholarship_type === filterType);
    }

    if (filterPublished !== 'all') {
      const isPublished = filterPublished === 'published';
      filtered = filtered.filter(sch => sch.published === isPublished);
    }

    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setFilteredScholarships(filtered);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this scholarship?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/scholarships/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete');

      setScholarships(prev => prev.filter(sch => sch.id !== id));
    } catch (err) {
      alert('Failed to delete scholarship');
    }
  };

  const togglePublish = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/scholarships/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ published: !currentStatus })
      });

      if (!response.ok) throw new Error('Failed to update');

      setScholarships(prev => 
        prev.map(sch => sch.id === id ? { ...sch, published: !currentStatus } : sch)
      );
    } catch (err) {
      alert('Failed to update publish status');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading scholarships...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2">Scholarships</h1>
            <p className="text-[#6B7280]">Manage scholarship content</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-6 py-3 bg-white border-2 border-[#1E40AF] text-[#1E40AF] hover:bg-blue-50 font-semibold rounded-lg transition-all flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Bulk Import
            </button>
            <button
              onClick={() => navigate('/admin/scholarships/new')}
              className="px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all flex items-center gap-2"
              data-testid="add-scholarship-button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Scholarship
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Provider</label>
              <select
                value={filterProvider}
                onChange={(e) => setFilterProvider(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="all">All Providers</option>
                <option value="University">University</option>
                <option value="Government">Government</option>
                <option value="NGO">NGO</option>
                <option value="Corporate">Corporate</option>
                <option value="Unisave">Unisave</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="all">All Types</option>
                <option value="Merit">Merit</option>
                <option value="Need">Need-Based</option>
                <option value="Entry">Entry</option>
                <option value="Early Bird">Early Bird</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Status</label>
              <select
                value={filterPublished}
                onChange={(e) => setFilterPublished(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-sm text-[#6B7280]">
              Showing {filteredScholarships.length} of {scholarships.length} scholarships
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl overflow-hidden">
          {filteredScholarships.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-[#6B7280]">No scholarships found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F8FAFC]">
                  <tr className="border-b-2 border-[#E5E7EB]">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Provider</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredScholarships.map((scholarship) => (
                    <tr
                      key={scholarship.id}
                      className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors"
                    >
                      <td className="py-3 px-4 text-sm font-medium text-[#111827]">{scholarship.scholarship_name}</td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">{scholarship.provider}</td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">{scholarship.scholarship_type}</td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => togglePublish(scholarship.id, scholarship.published)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            scholarship.published
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {scholarship.published ? 'Published' : 'Draft'}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/admin/scholarships/${scholarship.id}/edit`)}
                            className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(scholarship.id)}
                            className="text-red-600 hover:text-red-800 font-semibold text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Excel Upload Modal */}
      <ExcelUploadModal
        entity="scholarships"
        entityLabel="Scholarships"
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          fetchScholarships();
          setShowUploadModal(false);
        }}
      />
    </AdminLayout>
  );
};

export default ScholarshipList;